import React, { useMemo } from "react";
import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  Divider,
  Grid,
  Stack,
  Typography,
} from "@mui/material";
import { Calendar, FileSignature, ShieldCheck } from "lucide-react";
import { useNavigate } from "react-router-dom";

function B2BContracts() {
  const navigate = useNavigate();
  const brand = useMemo(
    () => ({
      bg: "#F7FAFC",
      text: "#0F172A",
      subtext: "rgba(15, 23, 42, 0.72)",
      stroke: "rgba(2, 6, 23, 0.10)",
      accent: "#0EA5E9",
      accent2: "#22C55E",
    }),
    []
  );

  const contracts = useMemo(
    () => [
      {
        vendor: "PackRight Supplies",
        category: "Packaging",
        discount: "Up to 12% off",
        renewal: "2026-09-30",
        image:
          "https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?w=200&h=200&fit=crop",
      },
      {
        vendor: "SafeGear Industrial",
        category: "Industrial",
        discount: "Tiered pricing",
        renewal: "2026-07-15",
        image:
          "https://images.unsplash.com/photo-1581092580497-e0d23cbdf1dc?w=200&h=200&fit=crop",
      },
      {
        vendor: "OfficeWorks Pro",
        category: "Office",
        discount: "Net-30 + bundles",
        renewal: "2027-01-10",
        image:
          "https://images.unsplash.com/photo-1520607162513-77705c0f0d4a?w=200&h=200&fit=crop",
      },
    ],
    []
  );

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: brand.bg, py: 6 }}>
      <Container maxWidth="lg">
        <Box
          sx={{
            borderRadius: 4,
            overflow: "hidden",
            border: `1px solid ${brand.stroke}`,
            backgroundImage:
              'linear-gradient(90deg, rgba(14,165,233,0.12), rgba(34,197,94,0.10)), url("https://images.unsplash.com/photo-1556761175-4b46a572b786?w=1400&h=360&fit=crop")',
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        >
          <Box sx={{ p: { xs: 3, md: 4 } }}>
            <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems={{ xs: "flex-start", md: "center" }} justifyContent="space-between">
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 950, color: brand.text }}>
                  Contracts
                </Typography>
                <Typography sx={{ color: brand.subtext, mt: 0.5 }}>
                  Manage vendor agreements, price lists, and renewals.
                </Typography>
                <Stack direction="row" spacing={1} sx={{ mt: 1.5, flexWrap: "wrap" }}>
                  <Chip icon={<ShieldCheck size={16} />} label="Approved vendors" />
                  <Chip icon={<FileSignature size={16} />} label="Contract price lists" />
                  <Chip icon={<Calendar size={16} />} label="Renewal reminders" />
                </Stack>
              </Box>
              <Stack direction="row" spacing={1}>
                <Button
                  variant="outlined"
                  onClick={() => navigate("/B2BRFQ")}
                  sx={{
                    borderRadius: 2.5,
                    textTransform: "none",
                    fontWeight: 950,
                    borderColor: brand.stroke,
                    color: brand.text,
                  }}
                >
                  Create RFQ
                </Button>
                <Button
                  variant="contained"
                  onClick={() => navigate("/B2BShop")}
                  sx={{
                    borderRadius: 2.5,
                    textTransform: "none",
                    fontWeight: 950,
                    color: "white",
                    background: `linear-gradient(135deg, ${brand.accent} 0%, ${brand.accent2} 100%)`,
                  }}
                >
                  Browse catalog
                </Button>
              </Stack>
            </Stack>
          </Box>
        </Box>

        <Grid container spacing={3} sx={{ mt: 1 }}>
          <Grid item xs={12} md={4}>
            <Card sx={{ borderRadius: 4, border: `1px solid ${brand.stroke}` }}>
              <CardContent>
                <Typography sx={{ fontWeight: 950, color: brand.text }}>
                  Summary
                </Typography>
                <Divider sx={{ my: 1.5 }} />
                <Stack spacing={1}>
                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="body2" sx={{ color: brand.subtext }}>
                      Active contracts
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 950, color: brand.text }}>
                      {contracts.length}
                    </Typography>
                  </Stack>
                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="body2" sx={{ color: brand.subtext }}>
                      Next renewal
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 950, color: brand.text }}>
                      2026-07-15
                    </Typography>
                  </Stack>
                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="body2" sx={{ color: brand.subtext }}>
                      Preferred categories
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 950, color: brand.text }}>
                      3
                    </Typography>
                  </Stack>
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={8}>
            <Card sx={{ borderRadius: 4, border: `1px solid ${brand.stroke}` }}>
              <CardContent>
                <Typography sx={{ fontWeight: 950, color: brand.text }}>
                  Vendor contracts
                </Typography>
                <Typography variant="body2" sx={{ color: brand.subtext, mb: 2 }}>
                  A clean list view with vendor visuals.
                </Typography>

                <Stack spacing={1.25}>
                  {contracts.map((c) => (
                    <Box
                      key={c.vendor}
                      sx={{
                        p: 1.75,
                        borderRadius: 3,
                        bgcolor: "white",
                        border: `1px solid ${brand.stroke}`,
                      }}
                    >
                      <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems={{ xs: "flex-start", sm: "center" }} justifyContent="space-between">
                        <Stack direction="row" spacing={1.5} alignItems="center">
                          <Avatar variant="rounded" src={c.image} sx={{ width: 52, height: 52 }} />
                          <Box sx={{ minWidth: 0 }}>
                            <Typography sx={{ fontWeight: 950, color: brand.text }} noWrap>
                              {c.vendor}
                            </Typography>
                            <Typography variant="body2" sx={{ color: brand.subtext }} noWrap>
                              {c.category} • {c.discount}
                            </Typography>
                          </Box>
                        </Stack>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Chip size="small" label={`Renewal: ${c.renewal}`} />
                          <Button
                            size="small"
                            variant="outlined"
                            sx={{ textTransform: "none", fontWeight: 900, borderColor: brand.stroke, color: brand.text }}
                          >
                            View
                          </Button>
                        </Stack>
                      </Stack>
                    </Box>
                  ))}
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}

export default B2BContracts;

